import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../Config.js';
import axios from 'axios';
import {
  CircleCheck,
  Eye,
  HandCoins,
  IndianRupee,
  Receipt,
  RefreshCw,
  Search,
  TriangleAlert,
  Users,
  X,
} from 'lucide-react';
import styles from './CustomerDueList.module.css';
import BillViewPopup from './BillViewPopup.js';

// Amount with Indian digit grouping, for display only
const formatAmount = (value) =>
  Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const NO_DUES_MESSAGE = 'No dues found.';

const CustomerDueList = () => {
  const [customerDueList, setCustomerDueList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showBillPopup, setShowBillPopup] = useState(false);
  const [selectedBillNo, setSelectedBillNo] = useState('');

  // Fetch the customer due list
  const fetchCustomerList = async () => {
    setIsLoading(true);
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      const storeId = user?.storeId;

      const response = await axios.get(`${API_BASE_URL}/user/dueList`, {
        params: { storeId },
      });

      // Add a paymentMode field to each due item
      const updatedData = response.data.map((item) => ({
        ...item,
        paymentMode: '',
      }));

      setCustomerDueList(updatedData);
      setFilteredList(updatedData);
      setError('');
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setError(NO_DUES_MESSAGE);
      } else {
        setError('An error occurred while fetching due.');
      }
      setCustomerDueList([]);
      setFilteredList([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerList();
  }, []);

  const handleFilterChange = (e) => {
    const value = e.target.value.toLowerCase();
    setFilter(value);

    const filtered = customerDueList.filter(
      (dues) =>
        dues.billNo.toString().toLowerCase().includes(value) ||
        dues.customerMobileNo.toString().toLowerCase().includes(value) ||
        dues.customerName.toLowerCase().includes(value)
    );
    setFilteredList(filtered);
  };

  const clearFilter = () => {
    setFilter('');
    setFilteredList(customerDueList);
  };

  const handlePaymentModeChange = (sno, mode) => {
    const updatedList = filteredList.map((item) =>
      item.sno === sno ? { ...item, paymentMode: mode } : item
    );
    setFilteredList(updatedList);
  };

  const handlePayment = async (paymentId) => {
    const selectedDue = filteredList.find((item) => item.sno === paymentId);

    if (!selectedDue?.paymentMode) {
      alert('Please select a payment mode.');
      return;
    }

    setIsLoading(true);
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      const storeId = user?.storeId;

      const paymentModel = {
        paymentId,
        paymentMode: selectedDue.paymentMode,
        storeId,
      };

      const response = await axios.post(`${API_BASE_URL}/user/due/payment`, paymentModel);

      if (response.status === 200) {
        alert(response.data);
        fetchCustomerList(); // Refresh the customer list
      } else {
        alert('Payment failed. Please try again.');
      }
    } catch (error) {
      alert('An error occurred while processing the payment.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewBill = (billNo) => {
    setSelectedBillNo(billNo);
    setShowBillPopup(true);
  };

  const handleCloseBillPopup = () => {
    setShowBillPopup(false);
    setSelectedBillNo(null);
  };

  const totalAmountSum = filteredList.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
  const customerCount = new Set(filteredList.map((d) => d.customerMobileNo)).size;
  const hasRows = filteredList.length > 0;
  // A 404 just means nobody owes anything — show it as an empty state, not an error
  const isNoDues = error === NO_DUES_MESSAGE;

  const summaryCards = [
    { key: 'amount', label: 'Total due amount', value: `₹${formatAmount(totalAmountSum)}`, icon: IndianRupee, primary: true },
    { key: 'dues', label: 'Dues', value: filteredList.length, icon: Receipt },
    { key: 'customers', label: 'Customers', value: customerCount, icon: Users },
  ];

  return (
    <>
      <div className={styles.page}>
        <header className={styles.header}>
          <div className={styles.titleBlock}>
            <span className={styles.headerIcon} aria-hidden="true">
              <HandCoins size={20} />
            </span>
            <h1 className={styles.title}>Customer Due List</h1>
            <div className={styles.chips}>
              {customerDueList.length > 0 ? (
                <span className={styles.chip}>
                  {customerDueList.length} pending {customerDueList.length === 1 ? 'due' : 'dues'}
                </span>
              ) : (
                <span className={styles.chipMuted}>{isLoading ? 'Loading…' : 'No pending dues'}</span>
              )}
              {filter && (
                <span className={styles.chip}>
                  <Search size={13} aria-hidden="true" />
                  {filter}
                </span>
              )}
            </div>
          </div>

          <div className={styles.headerActions}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnGhost}`}
              onClick={fetchCustomerList}
              disabled={isLoading}
            >
              <RefreshCw size={15} className={isLoading ? styles.spin : undefined} aria-hidden="true" />
              Refresh
            </button>
          </div>
        </header>

        {error && !isNoDues && (
          <div className={styles.errorBanner} role="alert">
            <TriangleAlert size={18} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {/* -------------------- Summary cards -------------------- */}
        <div className={styles.summaryGrid}>
          {summaryCards.map(({ key, label, value, icon: Icon, primary }) => (
            <div key={key} className={`${styles.summaryCard} ${primary ? styles.summaryCardPrimary : ''}`}>
              <span className={styles.summaryIcon} aria-hidden="true">
                <Icon size={18} />
              </span>
              <div className={styles.summaryText}>
                <span className={styles.summaryLabel}>{label}</span>
                <span className={styles.summaryValue}>{value}</span>
              </div>
            </div>
          ))}
        </div>

        {/* -------------------- Results card -------------------- */}
        <div className={styles.card}>
          <div className={styles.toolbar}>
            <div className={styles.searchWrap}>
              <Search size={15} className={styles.searchIcon} aria-hidden="true" />
              <input
                className={styles.searchInput}
                type="text"
                placeholder="Search by bill no, mobile no or customer name"
                value={filter}
                onChange={handleFilterChange}
                aria-label="Search dues"
              />
              {filter && (
                <button
                  type="button"
                  className={`${styles.iconBtn} ${styles.clearSearch}`}
                  onClick={clearFilter}
                  aria-label="Clear search"
                >
                  <X size={15} aria-hidden="true" />
                </button>
              )}
            </div>
            {customerDueList.length > 0 && (
              <span className={styles.resultCount}>
                Showing {filteredList.length} of {customerDueList.length}
              </span>
            )}
          </div>

          <div className={styles.tableWrap}>
            {isLoading && (
              <div className={styles.loadingOverlay}>
                <span className={styles.spinner} aria-hidden="true" />
                <span className={styles.loadingText}>Loading dues…</span>
              </div>
            )}

            {hasRows ? (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Bill No</th>
                    <th>Date</th>
                    <th>Customer Name</th>
                    <th>Customer Mobile</th>
                    <th className={styles.num}>Amount</th>
                    <th>Payment Mode</th>
                    <th className={styles.alignCenter}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredList.map((dues) => (
                    <tr key={dues.sno}>
                      <td className={styles.strong}>{dues.billNo}</td>
                      <td>{new Date(dues.date).toLocaleDateString('en-GB')}</td>
                      <td className={styles.strong}>{dues.customerName}</td>
                      <td>{dues.customerMobileNo}</td>
                      <td className={`${styles.num} ${styles.strong}`}>{formatAmount(dues.amount)}</td>
                      <td>
                        <select
                          className={styles.rowSelect}
                          value={dues.paymentMode}
                          onChange={(e) => handlePaymentModeChange(dues.sno, e.target.value)}
                          disabled={isLoading}
                          aria-label={`Payment mode for bill ${dues.billNo}`}
                        >
                          <option value="" disabled>
                            Select
                          </option>
                          <option value="CASH">Cash</option>
                          <option value="UPI">UPI</option>
                          <option value="CARD">Card</option>
                        </select>
                      </td>
                      <td className={styles.alignCenter}>
                        <div className={styles.rowActions}>
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnSm} ${styles.btnPay}`}
                            onClick={() => handlePayment(dues.sno)}
                            disabled={isLoading}
                          >
                            Pay
                          </button>
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`}
                            onClick={() => handleViewBill(dues.billNo)}
                            disabled={isLoading}
                          >
                            <Eye size={14} aria-hidden="true" />
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="4">Total</td>
                    <td className={styles.num}>{formatAmount(totalAmountSum)}</td>
                    <td colSpan="2" />
                  </tr>
                </tfoot>
              </table>
            ) : (
              !isLoading &&
              !(error && !isNoDues) && (
                <div className={styles.noResults}>
                  {filter && customerDueList.length > 0 ? (
                    <>
                      <span className={styles.noResultsIcon} aria-hidden="true">
                        <Search size={28} />
                      </span>
                      <p className={styles.noResultsTitle}>No matching dues</p>
                      <p className={styles.noResultsText}>
                        Nothing matches “{filter}”. Try another bill no, mobile no or name.
                      </p>
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.btnGhost} ${styles.noResultsAction}`}
                        onClick={clearFilter}
                      >
                        <X size={15} aria-hidden="true" />
                        Clear search
                      </button>
                    </>
                  ) : (
                    <>
                      <span className={styles.noResultsIcon} aria-hidden="true">
                        <CircleCheck size={28} />
                      </span>
                      <p className={styles.noResultsTitle}>No pending dues</p>
                      <p className={styles.noResultsText}>Every customer bill has been paid.</p>
                    </>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {/* Rendered outside the page so its overlay isn't trapped by the page container */}
      {showBillPopup && (
        <BillViewPopup show={showBillPopup} onHide={handleCloseBillPopup} billNo={selectedBillNo} />
      )}
    </>
  );
};

export default CustomerDueList;
